import React, { useId } from "react";
import {
  Activity,
  ArrowUpRight,
  BatteryCharging,
  BrainCircuit,
  CircleArrowOutUpRight,
  Gauge,
  HeartPulse,
  RefreshCcw,
  Repeat2,
  Route,
  Waves,
  Zap,
} from "lucide-react";

const DEPENDENCY_NODES = [
  {
    key: "trigger",
    label: "Trigger or Future Demand",
    sub: "A cue enters the system",
    icon: Zap,
    angle: -90,
  },
  {
    key: "learned-pattern",
    label: "Learned Adaptive Pattern",
    sub: "The trained route becomes available",
    icon: BrainCircuit,
    angle: -30,
  },
  {
    key: "sympathetic",
    label: "Sympathetic Activation",
    sub: "The system mobilizes",
    icon: Activity,
    angle: 30,
  },
  {
    key: "emotion",
    label: "Emotional Reactivity",
    sub: "Activation becomes urgency",
    icon: Waves,
    angle: 90,
  },
  {
    key: "outsourced-relief",
    label: "Outsourced Relief",
    sub: "The state is changed externally",
    icon: CircleArrowOutUpRight,
    angle: 150,
  },
  {
    key: "reinforcement",
    label: "Dependency Reinforcement",
    sub: "The route becomes easier to repeat",
    icon: Repeat2,
    angle: 210,
  },
];

const ADAPTIVE_NODES = [
  {
    key: "future-demand",
    label: "Future Demand",
    sub: "A real demand enters the system",
    icon: ArrowUpRight,
    angle: -90,
  },
  {
    key: "sympathetic",
    label: "Sympathetic Activation",
    sub: "The system mobilizes",
    icon: Activity,
    angle: -30,
  },
  {
    key: "emotion",
    label: "Emotional Reactivity",
    sub: "The demand is felt",
    icon: Waves,
    angle: 30,
  },
  {
    key: "intervention",
    label: "Matched Adaptive Intervention",
    sub: "A physiological input redirects the route",
    icon: Route,
    angle: 90,
  },
  {
    key: "parasympathetic",
    label: "Parasympathetic Recovery",
    sub: "Restoration is supported",
    icon: HeartPulse,
    angle: 150,
  },
  {
    key: "recovery",
    label: "Recovery",
    sub: "The activation cycle completes",
    icon: BatteryCharging,
    angle: 210,
  },
];

const getPoint = (angle, radius = 210) => {
  const radians = (angle * Math.PI) / 180;
  return {
    x: 300 + radius * Math.cos(radians),
    y: 300 + radius * Math.sin(radians),
  };
};

const DEPENDENCY_FLOW_NODES = [
  {
    key: "trigger",
    label: "Trigger or Future Demand",
    sub: "A cue enters the system",
    icon: Zap,
    left: 74,
    top: 8,
    width: 40,
  },
  {
    key: "learned-pattern",
    label: "Learned Adaptive Pattern",
    sub: "The trained route becomes available",
    icon: BrainCircuit,
    left: 62,
    top: 24,
    width: 42,
  },
  {
    key: "sympathetic",
    label: "Sympathetic Activation",
    sub: "The system mobilizes",
    icon: Activity,
    left: 49,
    top: 40,
    width: 40,
  },
  {
    key: "emotion",
    label: "Emotional Reactivity",
    sub: "Activation becomes urgency",
    icon: Waves,
    left: 36,
    top: 56,
    width: 38,
  },
  {
    key: "outsourced-relief",
    label: "Outsourced Relief",
    sub: "The state is changed externally",
    icon: CircleArrowOutUpRight,
    left: 23,
    top: 72,
    width: 36,
  },
  {
    key: "reinforcement",
    label: "Dependency Reinforcement",
    sub: "The route becomes easier to repeat",
    icon: Repeat2,
    left: 70,
    top: 91,
    width: 45,
  },
];

const DependencyFlowDiagram = ({
  size = "hero",
  showDescriptions = true,
  testId,
}) => {
  const rawId = useId();
  const uid = rawId.replace(/:/g, "");
  const compact = size === "compact";
  const widthClass =
    size === "compact"
      ? "w-full max-w-[390px]"
      : size === "diagram"
        ? "w-full max-w-[500px]"
        : "w-full max-w-[560px]";

  return (
    <div
      data-testid={testId || "dependency-loop"}
      className={`relative ${widthClass} mx-auto aspect-[700/760]`}
      style={{ color: "#F4F1EA" }}
    >
      <div
        className="absolute inset-[4%] rounded-[34%] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 58% 56%, rgba(155,164,170,0.14) 0%, transparent 64%)",
        }}
      />

      <svg
        viewBox="0 0 700 760"
        className="absolute inset-0 h-full w-full overflow-visible"
        role="img"
        aria-labelledby={`${uid}-title ${uid}-description`}
      >
        <title id={`${uid}-title`}>The Dependency Loop</title>
        <desc id={`${uid}-description`}>
          A nonlinear pathway from trigger or future demand to dependency
          reinforcement. Reinforcement can re-enter at the trigger, learned
          adaptive pattern, sympathetic activation, or emotional reactivity.
        </desc>

        <defs>
          <linearGradient id={`${uid}-primary`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F0F3F4" stopOpacity="0.92" />
            <stop offset="100%" stopColor="#8E989D" stopOpacity="0.72" />
          </linearGradient>
          <linearGradient id={`${uid}-return`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#B8C1C5" stopOpacity="0.78" />
            <stop offset="100%" stopColor="#F4F7F8" stopOpacity="0.96" />
          </linearGradient>
          <marker
            id={`${uid}-arrow-primary`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth={compact ? "5" : "7"}
            markerHeight={compact ? "5" : "7"}
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#DDE2E4" />
          </marker>
          <marker
            id={`${uid}-arrow-return`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth={compact ? "5.5" : "8"}
            markerHeight={compact ? "5.5" : "8"}
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#F4F7F8" />
          </marker>
          <filter id={`${uid}-line-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g
          fill="none"
          stroke={`url(#${uid}-primary)`}
          strokeWidth={compact ? "1.5" : "2"}
          strokeLinecap="round"
          markerEnd={`url(#${uid}-arrow-primary)`}
        >
          <path d="M 520 95 C 468 112, 440 132, 434 154" />
          <path d="M 410 217 C 350 232, 332 252, 336 276" />
          <path d="M 312 339 C 262 354, 248 375, 252 398" />
          <path d="M 224 461 C 185 478, 169 497, 170 520" />
          <path d="M 170 585 C 174 624, 273 627, 357 665" />
        </g>

        <g
          fill="none"
          stroke={`url(#${uid}-return)`}
          strokeLinecap="round"
          filter={`url(#${uid}-line-glow)`}
          markerEnd={`url(#${uid}-arrow-return)`}
        >
          <path
            d="M 615 666 C 681 624, 680 156, 532 94"
            strokeWidth={compact ? "1.8" : "2.5"}
          />
          <path
            d="M 555 660 C 617 554, 611 298, 466 216"
            strokeWidth={compact ? "1.65" : "2.3"}
            strokeDasharray="10 8"
          />
          <path
            d="M 493 658 C 526 565, 503 404, 385 340"
            strokeWidth={compact ? "1.55" : "2.2"}
            strokeDasharray="4 7"
          />
          <path
            d="M 429 659 C 414 588, 376 508, 292 461"
            strokeWidth={compact ? "1.5" : "2.1"}
            strokeDasharray="2 7"
          />
        </g>

        <g transform="translate(523 602)">
          <rect
            x="-94"
            y="-17"
            width="188"
            height="34"
            rx="17"
            fill="#0A0C0D"
            stroke="#4B5357"
          />
          <text
            x="0"
            y="4"
            textAnchor="middle"
            fill="#DDE2E4"
            fontFamily="General Sans, Manrope, sans-serif"
            fontSize={compact ? "10" : "11"}
            fontWeight="500"
            letterSpacing="1.1"
          >
            RE-ENTRY PATHWAYS
          </text>
        </g>
      </svg>

      {DEPENDENCY_FLOW_NODES.map((node, index) => (
        <div
          key={node.key}
          data-testid={`dependency-loop-node-${node.key}`}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${node.left}%`,
            top: `${node.top}%`,
            width: `${node.width}%`,
          }}
        >
          <div
            className={`relative flex items-center rounded-xl border ${
              compact ? "gap-2 px-2.5 py-2" : "gap-3 px-3.5 py-3"
            }`}
            style={{
              minHeight: compact ? 42 : 56,
              color: "#F4F1EA",
              background:
                index === DEPENDENCY_FLOW_NODES.length - 1
                  ? "linear-gradient(145deg, #272C2F 0%, #121416 100%)"
                  : "linear-gradient(145deg, #222629 0%, #151719 100%)",
              borderColor:
                index === DEPENDENCY_FLOW_NODES.length - 1
                  ? "#879095"
                  : "#555D61",
              boxShadow:
                index === DEPENDENCY_FLOW_NODES.length - 1
                  ? "0 14px 34px -14px rgba(0,0,0,0.9), 0 0 20px rgba(170,180,185,0.12)"
                  : "0 12px 28px -16px rgba(0,0,0,0.9)",
            }}
          >
            <div
              className={`shrink-0 rounded-full flex items-center justify-center border ${
                compact ? "h-6 w-6" : "h-8 w-8"
              }`}
              style={{
                color: "#DDE2E4",
                backgroundColor: "#0B0D0E",
                borderColor: "#4D5559",
              }}
            >
              <node.icon
                size={compact ? 12 : 15}
                strokeWidth={1.55}
              />
            </div>
            <div className="min-w-0">
              <div
                className={`font-display font-medium leading-tight ${
                  compact ? "text-[9px] sm:text-[10px]" : "text-[11px] sm:text-xs"
                }`}
              >
                {node.label}
              </div>
              {showDescriptions && !compact && (
                <div className="mt-1 text-[8px] sm:text-[9px] font-light leading-snug text-[#9CA4A8]">
                  {node.sub}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CircularLoopDiagram = ({
  variant = "adaptive",
  size = "hero",
  showDescriptions = true,
  testId,
}) => {
  const rawId = useId();
  const uid = rawId.replace(/:/g, "");
  const dependency = variant === "dependency";
  const nodes = dependency ? DEPENDENCY_NODES : ADAPTIVE_NODES;
  const palette = dependency
    ? {
        canvas: "#111315",
        surface: "#1A1D20",
        surfaceStrong: "#08090A",
        ink: "#F4F1EA",
        soft: "#AEB4B7",
        faint: "#5F666A",
        line: "#343A3E",
        lineSoft: "#252A2D",
        accent: "#8E969B",
        accentStrong: "#D7DCDE",
        glow: "rgba(145, 154, 160, 0.17)",
      }
    : {
        canvas: "#F7F5F0",
        surface: "#FFFFFF",
        surfaceStrong: "#FFFFFF",
        ink: "#0A1F26",
        soft: "#6E7F86",
        faint: "#D4CCBC",
        line: "#E4DED2",
        lineSoft: "#EDE8DC",
        accent: "#0FB5A6",
        accentStrong: "#068073",
        glow: "rgba(15, 181, 166, 0.16)",
      };

  const widthClass =
    size === "compact"
      ? "w-full max-w-[390px]"
      : size === "diagram"
        ? "w-full max-w-[500px]"
        : "w-full max-w-[560px]";
  const nodeRadius = size === "compact" ? 26 : 30;
  const reinforcement = getPoint(DEPENDENCY_NODES[5].angle);
  const reentryTargets = DEPENDENCY_NODES.slice(0, 4).map((node) =>
    getPoint(node.angle),
  );

  return (
    <div
      data-testid={testId || `${variant}-loop`}
      className={`relative aspect-square ${widthClass} mx-auto`}
      style={{ color: palette.ink, marginBottom: "8.6%" }}
    >
      <div
        className="absolute inset-[3%] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${palette.glow} 0%, transparent 68%)`,
        }}
      />

      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-labelledby={`${uid}-title ${uid}-description`}
      >
        <title id={`${uid}-title`}>
          {dependency ? "The Dependency Loop" : "The Adaptive Loop"}
        </title>
        <desc id={`${uid}-description`}>
          {dependency
            ? "A nonlinear loop in which dependency reinforcement can reactivate the trigger, learned adaptive pattern, sympathetic activation, or emotional reactivity."
            : "A structured loop that moves from future demand through activation, matched intervention, parasympathetic recovery, recovery, and homeostasis."}
        </desc>

        <defs>
          <linearGradient id={`${uid}-arc`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={palette.accentStrong} stopOpacity="0.95" />
            <stop offset="55%" stopColor={palette.accent} stopOpacity="0.78" />
            <stop offset="100%" stopColor={palette.accent} stopOpacity="0.3" />
          </linearGradient>
          <marker
            id={`${uid}-arrow`}
            viewBox="0 0 10 10"
            refX="7"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={palette.accent} />
          </marker>
          <filter id={`${uid}-shadow`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="14"
              floodColor={dependency ? "#000000" : "#0A1F26"}
              floodOpacity={dependency ? "0.35" : "0.09"}
            />
          </filter>
        </defs>

        <circle
          cx="300"
          cy="300"
          r="278"
          fill="none"
          stroke={palette.line}
          strokeWidth="1"
          strokeDasharray={dependency ? "1 8" : "2 5"}
          className="forr-loop-counter"
          style={{ transformOrigin: "300px 300px" }}
        />
        <circle
          cx="300"
          cy="300"
          r="246"
          fill="none"
          stroke={palette.lineSoft}
          strokeWidth="1"
        />
        <circle
          cx="300"
          cy="300"
          r="178"
          fill="none"
          stroke={palette.lineSoft}
          strokeWidth="1"
        />

        <circle
          cx="300"
          cy="300"
          r="210"
          fill="none"
          stroke={`url(#${uid}-arc)`}
          strokeWidth={dependency ? "1.3" : "2"}
          strokeDasharray={dependency ? "3 9" : "5 7"}
          markerEnd={`url(#${uid}-arrow)`}
          className={dependency ? undefined : "forr-flow-dash"}
        />

        {dependency &&
          reentryTargets.map((target, index) => {
            const controls = [
              { x: 185, y: 265 },
              { x: 245, y: 215 },
              { x: 335, y: 225 },
              { x: 390, y: 305 },
            ];
            const control = controls[index];
            return (
              <path
                key={`reentry-${index}`}
                d={`M ${reinforcement.x} ${reinforcement.y} Q ${control.x} ${control.y} ${target.x} ${target.y}`}
                fill="none"
                stroke={palette.accent}
                strokeWidth="1"
                strokeOpacity={0.24 + index * 0.06}
                strokeDasharray="4 7"
                markerEnd={`url(#${uid}-arrow)`}
              />
            );
          })}

        {!dependency &&
          nodes.map((node, index) => {
            const next = nodes[(index + 1) % nodes.length];
            const start = getPoint(node.angle);
            const end = getPoint(next.angle);
            return (
              <path
                key={`chord-${node.key}`}
                d={`M ${start.x} ${start.y} Q 300 300 ${end.x} ${end.y}`}
                fill="none"
                stroke={palette.accent}
                strokeWidth="0.7"
                strokeOpacity="0.09"
              />
            );
          })}

        <g filter={`url(#${uid}-shadow)`}>
          <circle
            cx="300"
            cy="300"
            r="70"
            fill={palette.surfaceStrong}
            stroke={dependency ? palette.faint : palette.line}
            strokeWidth="1"
          />
          <circle
            cx="300"
            cy="300"
            r="62"
            fill="none"
            stroke={palette.accent}
            strokeWidth="1"
            strokeOpacity={dependency ? "0.42" : "0.38"}
            strokeDasharray={dependency ? "2 7" : "3 5"}
            className="forr-loop-rotate"
            style={{ transformOrigin: "300px 300px" }}
          />
        </g>

        {dependency ? (
          <RefreshCcw
            x="286"
            y="256"
            width="28"
            height="28"
            color={palette.accentStrong}
            strokeWidth="1.4"
          />
        ) : (
          <Gauge
            x="286"
            y="256"
            width="28"
            height="28"
            color={palette.accentStrong}
            strokeWidth="1.4"
          />
        )}
        <text
          x="300"
          y="304"
          textAnchor="middle"
          fill={palette.ink}
          fontFamily="General Sans, Manrope, sans-serif"
          fontSize="13"
          fontWeight="500"
        >
          Centre
        </text>
        <text
          x="300"
          y="326"
          textAnchor="middle"
          fill={palette.accentStrong}
          fontFamily="General Sans, Manrope, sans-serif"
          fontSize="16"
          fontWeight="600"
        >
          {dependency ? "Dependency" : "Homeostasis"}
        </text>

        {nodes.map((node) => {
          const point = getPoint(node.angle);
          return (
            <g key={node.key} transform={`translate(${point.x} ${point.y})`}>
              <circle
                r={nodeRadius + 7}
                fill={palette.canvas}
                stroke={palette.line}
                strokeWidth="1"
              />
              <circle
                r={nodeRadius}
                fill={palette.surface}
                stroke={dependency ? palette.faint : palette.line}
                strokeWidth="1"
              />
            </g>
          );
        })}
      </svg>

      {nodes.map((node) => {
        const point = getPoint(node.angle);
        const left = (point.x / 600) * 100;
        const top = (point.y / 600) * 100;
        return (
          <div
            key={node.key}
            data-testid={`${variant}-loop-node-${node.key}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${left}%`, top: `${top}%` }}
          >
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: nodeRadius * 2,
                height: nodeRadius * 2,
                color: palette.accentStrong,
              }}
            >
              <node.icon size={size === "compact" ? 15 : 18} strokeWidth={1.55} />
            </div>
            <div
              className="absolute top-full mt-3 text-center"
              style={{ width: size === "compact" ? 112 : 132 }}
            >
              <div
                className="font-display text-[11px] sm:text-[12px] font-medium leading-tight tracking-[-0.01em]"
                style={{ color: palette.ink }}
              >
                {node.label}
              </div>
              {showDescriptions && size !== "compact" && (
                <div
                  className="mt-1 text-[9px] sm:text-[10px] font-light leading-snug"
                  style={{ color: palette.soft }}
                >
                  {node.sub}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const LoopDiagram = (props) =>
  props.variant === "dependency" ? (
    <DependencyFlowDiagram {...props} />
  ) : (
    <CircularLoopDiagram {...props} />
  );

export const LoopPair = ({ size = "hero", compactCopy = false }) => (
  <div className="grid lg:grid-cols-2 gap-5 lg:gap-8 items-stretch">
    <article
      className="relative overflow-hidden rounded-[30px] sm:rounded-[36px] p-4 sm:p-7 lg:p-8"
      style={{
        background:
          "radial-gradient(circle at 50% 38%, #262B2F 0%, #121416 52%, #090A0B 100%)",
        border: "1px solid #30363A",
        boxShadow: "0 28px 80px -42px rgba(0,0,0,0.85)",
      }}
    >
      <div className="flex items-center justify-between gap-4 px-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#929A9F]">
            The reinforced route
          </div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-[-0.02em] text-[#F4F1EA]">
            The Dependency Loop
          </h3>
        </div>
        <div className="h-2 w-2 rounded-full bg-[#899196] shadow-[0_0_18px_rgba(170,180,185,0.6)]" />
      </div>
      <LoopDiagram
        variant="dependency"
        size={size}
        showDescriptions={!compactCopy}
      />
      <p className="mx-auto -mt-1 max-w-xl px-3 pb-3 text-sm font-light leading-relaxed text-[#AEB4B7]">
        Reinforcement can re-enter as a trigger or future demand, the learned
        pattern, sympathetic activation, or emotional reactivity.
      </p>
    </article>

    <article
      className="relative overflow-hidden rounded-[30px] sm:rounded-[36px] p-4 sm:p-7 lg:p-8"
      style={{
        background:
          "radial-gradient(circle at 50% 38%, rgba(214,241,237,0.75) 0%, #FBF9F4 50%, #F7F5F0 100%)",
        border: "1px solid var(--forr-line)",
        boxShadow: "var(--forr-shadow-lg)",
      }}
    >
      <div className="flex items-center justify-between gap-4 px-2">
        <div>
          <div className="forr-overline">The recovery route</div>
          <h3 className="mt-2 font-display forr-text-ink text-2xl sm:text-3xl font-medium tracking-[-0.02em]">
            The Adaptive Loop
          </h3>
        </div>
        <div
          className="h-2 w-2 rounded-full"
          style={{
            backgroundColor: "var(--forr-teal)",
            boxShadow: "0 0 18px var(--forr-teal)",
          }}
        />
      </div>
      <LoopDiagram
        variant="adaptive"
        size={size}
        showDescriptions={!compactCopy}
      />
      <p className="mx-auto -mt-1 max-w-xl px-3 pb-3 text-sm font-light leading-relaxed forr-text-soft">
        A matched physiological input gives activation a structured route toward
        recovery and homeostatic regulation.
      </p>
    </article>
  </div>
);

export default LoopDiagram;
